import { motion, AnimatePresence } from "framer-motion";
import {
    Home,
    Wallet,
    CreditCard,
    TrendingUp,
    User,
    HelpCircle,
    LogOut,
    ChevronRight,
    ChevronLeft,
    PanelLeft,
    PanelLeftClose,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCustomer } from "@/lib/customer-context";
import { useNavigation } from "@/hooks/use-navigation";
import { Link } from "wouter";
import { cn } from "@/lib/utils";

interface CustomerSidebarProps {
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    topOffset?: boolean;
}

export function CustomerSidebar({ isCollapsed, onToggleCollapse, topOffset = false }: CustomerSidebarProps) {
    const { customer, logout } = useCustomer();
    const { isActive } = useNavigation();

    const initials = customer?.firstName && customer?.lastName
        ? `${customer.firstName[0]}${customer.lastName[0]}`
        : "U";

    const menuItems = [
        { icon: Home, label: "Home", href: "/portal/home" },
        { icon: Wallet, label: "My Wallet", href: "/portal/wallet" },
        { icon: CreditCard, label: "My Cards", href: "/portal/cards" },
        { icon: TrendingUp, label: "Transactions", href: "/portal/transactions" },
        { icon: User, label: "Account Settings", href: "/portal/account" },
        { icon: HelpCircle, label: "Help & Support", href: "/portal/help" },
    ];

    return (
        <motion.aside
            initial={false}
            animate={{ width: isCollapsed ? 80 : 280 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className={cn(
                "fixed left-0 bottom-0 bg-card border-r border-border z-30 hidden md:flex flex-col overflow-hidden",
                topOffset ? "top-[44px]" : "top-0"
            )}
        >
            {/* Logo & Toggle — mt-16 clears the fixed header (h-16 / z-40) */}
            <div className={cn(
                "flex-shrink-0 flex items-center mt-16",
                isCollapsed ? "justify-center p-3" : "justify-between px-4 py-3"
            )}>
                <AnimatePresence mode="wait">
                    {!isCollapsed && (
                        <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                            <Link href="/portal/home">
                                <img
                                    src="/vedalogic-logo.svg"
                                    alt="VedaLogic"
                                    className="h-12 invert dark:invert-0 cursor-pointer"
                                />
                            </Link>
                        </motion.div>
                    )}
                </AnimatePresence>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-md hover:bg-accent transition-colors duration-200 cursor-pointer shrink-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleCollapse();
                    }}
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                    {isCollapsed ? (
                        <PanelLeft className="h-5 w-5" />
                    ) : (
                        <PanelLeftClose className="h-5 w-5" />
                    )}
                </Button>
            </div>

            {/* User Info */}
            <div className={cn("p-4 pb-3 flex-shrink-0")}>
                <div className={cn("flex items-center", isCollapsed ? "justify-center" : "gap-3")}>
                    <Avatar className={cn("shrink-0 transition-all duration-300", isCollapsed ? "h-10 w-10" : "h-12 w-12")}>
                        <AvatarImage src={customer?.avatarUrl || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                            {initials}
                        </AvatarFallback>
                    </Avatar>
                    <AnimatePresence mode="wait">
                        {!isCollapsed && (
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="flex-1 text-left overflow-hidden"
                            >
                                <p className="font-semibold text-sm truncate">
                                    {customer?.firstName} {customer?.lastName}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{customer?.phone}</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>


            <Separator />

            {/* Navigation Menu */}
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {menuItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                        <motion.div
                            whileHover={{ x: isCollapsed ? 0 : 4 }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ duration: 0.15 }}
                        >
                            <Button
                                variant={isActive(item.href) ? "secondary" : "ghost"}
                                className={cn(
                                    "w-full h-11 transition-all",
                                    isCollapsed ? "justify-center px-0" : "justify-start px-3",
                                    isActive(item.href) && "bg-primary/10 text-primary font-medium"
                                )}
                                title={isCollapsed ? item.label : undefined}
                            >
                                <item.icon className={cn("h-5 w-5 shrink-0", !isCollapsed && "mr-3")} />
                                <AnimatePresence mode="wait">
                                    {!isCollapsed && (
                                        <motion.span
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -10 }}
                                            transition={{ duration: 0.2, ease: "easeOut" }}
                                            className="truncate"
                                        >
                                            {item.label}
                                        </motion.span>
                                    )}
                                </AnimatePresence>
                                {!isCollapsed && <ChevronRight className="h-4 w-4 ml-auto opacity-50 shrink-0" />}
                            </Button>
                        </motion.div>
                    </Link>
                ))}
            </nav>

            <Separator />

            {/* Logout Button */}
            <div className="p-3">
                <Button
                    variant="outline"
                    className={cn(
                        "w-full h-11 text-red-600 hover:text-red-700 hover:bg-red-50 transition-all",
                        isCollapsed ? "justify-center px-0" : "justify-start px-3"
                    )}
                    onClick={logout}
                    title={isCollapsed ? "Logout" : undefined}
                >
                    <LogOut className={cn("h-5 w-5 shrink-0", !isCollapsed && "mr-3")} />
                    <AnimatePresence mode="wait">
                        {!isCollapsed && (
                            <motion.span
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                            >
                                Logout
                            </motion.span>
                        )}
                    </AnimatePresence>
                </Button>
            </div>

            {/* Footer */}
            <AnimatePresence mode="wait">
                {!isCollapsed && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="p-4 pt-3 border-t bg-muted/30 overflow-hidden"
                    >
                        <p className="text-xs text-muted-foreground text-center">
                            VedaLogic Virtual Cards
                            <br />
                            © 2026 All rights reserved
                        </p>
                        <p className="text-[10px] text-muted-foreground/60 text-center mt-1">
                            Powered by VedaLogic Consulting
                        </p>
                        <p className="text-[9px] text-muted-foreground/30 text-center mt-0.5 font-mono">
                            v1.0.0-rc.3
                        </p>
                        <a href="https://vedalogic.betteruptime.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 text-[9px] text-emerald-500/60 hover:text-emerald-500 transition-colors mt-0.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            System Status
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.aside>
    );
}
